import { defineDspModule, type DspModuleInstance, type DspProcessBlock } from '../../dsp-module-contract/src/index.js';

export const SUPPORTED_PRNG_VERSIONS = Object.freeze(['xorshift32-v1', 'mulberry32-v1'] as const);
export type PrngVersion = (typeof SUPPORTED_PRNG_VERSIONS)[number];
export type Seed = number | string;

export interface SeedTreeOptions {
  readonly version?: PrngVersion;
}

export interface WeightedChoice<T> {
  readonly value: T;
  readonly weight: number;
}

const UINT32_RANGE = 0x1_0000_0000;
const FALLBACK_NON_ZERO_SEED = 0x6d2b_79f5;
const NAME_PATTERN = /^[^/\u0000]+(?:\/[^/\u0000]+)*$/;
const utf8 = new TextEncoder();

const assertFiniteBounds = (minimum: number, maximum: number): void => {
  if (!Number.isFinite(minimum) || !Number.isFinite(maximum)) throw new RangeError('distribution bounds must be finite');
  if (minimum > maximum) throw new RangeError('distribution bounds are reversed');
};

const interpolateBounds = (minimum: number, maximum: number, amount: number): number => (
  Math.max(minimum, Math.min(maximum, minimum * (1 - amount) + maximum * amount))
);

const assertName = (name: string): void => {
  if (!NAME_PATTERN.test(name)) throw new RangeError('stream name must contain non-empty slash-separated segments');
};

const canonicalSeed = (seed: Seed): string => {
  if (typeof seed === 'number') {
    if (!Number.isInteger(seed) || seed < 0 || seed >= UINT32_RANGE) throw new RangeError('numeric seed must be a uint32 integer');
    return String(seed);
  }
  if (typeof seed !== 'string' || seed.length === 0 || seed.includes('\u0000')) {
    throw new RangeError('string seed must be non-empty and must not contain NUL');
  }
  return seed;
};

/** Stable FNV-1a over UTF-8; the zero remap keeps xorshift streams live. */
const hashSeed = (value: string): number => {
  let hash = 0x811c_9dc5;
  for (const byte of utf8.encode(value)) {
    hash ^= byte;
    hash = Math.imul(hash, 0x0100_0193) >>> 0;
  }
  return hash === 0 ? FALLBACK_NON_ZERO_SEED : hash;
};

const isVersion = (value: string): value is PrngVersion => (
  (SUPPORTED_PRNG_VERSIONS as readonly string[]).includes(value)
);

export class RandomStream {
  readonly version: PrngVersion;
  readonly seed: number;

  private state: number;
  private correlatedState = 0;
  private hasCorrelatedState = false;

  constructor(seed: number, version: PrngVersion) {
    if (!Number.isInteger(seed) || seed < 0 || seed >= UINT32_RANGE) {
      throw new RangeError('stream seed must be a uint32 integer');
    }
    if (!isVersion(version)) throw new RangeError(`unsupported PRNG version: ${String(version)}`);
    this.seed = seed >>> 0 || FALLBACK_NON_ZERO_SEED;
    this.version = version;
    this.state = this.seed;
  }

  reset(): void {
    this.state = this.seed;
    this.correlatedState = 0;
    this.hasCorrelatedState = false;
  }

  nextUint32(): number {
    if (this.version === 'xorshift32-v1') {
      let value = this.state;
      value ^= value << 13;
      value ^= value >>> 17;
      value ^= value << 5;
      this.state = value >>> 0;
      return this.state;
    }

    this.state = (this.state + 0x6d2b_79f5) >>> 0;
    let value = this.state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return (value ^ (value >>> 14)) >>> 0;
  }

  next(): number {
    return this.nextUint32() / UINT32_RANGE;
  }

  uniform(minimum = 0, maximum = 1): number {
    assertFiniteBounds(minimum, maximum);
    if (minimum === maximum) return minimum;
    return interpolateBounds(minimum, maximum, this.next());
  }

  logarithmic(minimum: number, maximum: number): number {
    assertFiniteBounds(minimum, maximum);
    if (!(minimum > 0) || !(maximum > 0)) throw new RangeError('logarithmic bounds must be positive');
    if (minimum === maximum) return minimum;
    return Math.exp(this.uniform(Math.log(minimum), Math.log(maximum)));
  }

  weighted<T>(choices: readonly WeightedChoice<T>[]): T {
    if (choices.length === 0) throw new RangeError('weighted choice list must not be empty');
    let total = 0;
    for (const choice of choices) {
      if (!Number.isFinite(choice.weight) || choice.weight < 0) throw new RangeError('choice weight must be finite and non-negative');
      total += choice.weight;
    }
    if (!(total > 0) || !Number.isFinite(total)) throw new RangeError('weighted choices must have a finite positive total');
    const target = this.next() * total;
    let cumulative = 0;
    for (const choice of choices) {
      cumulative += choice.weight;
      if (target < cumulative) return choice.value;
    }
    return choices[choices.length - 1]!.value;
  }

  /**
   * Bounded AR(1)-style draw. State belongs to this stream instance, so reset
   * and fresh named streams reproduce the sequence without shared state.
   */
  correlated(minimum: number, maximum: number, correlation: number): number {
    assertFiniteBounds(minimum, maximum);
    if (!Number.isFinite(correlation) || correlation < -1 || correlation > 1) {
      throw new RangeError('correlation must be finite and in [-1, 1]');
    }
    if (minimum === maximum) return minimum;
    const innovation = this.next() * 2 - 1;
    if (!this.hasCorrelatedState) {
      this.correlatedState = innovation;
      this.hasCorrelatedState = true;
    } else {
      this.correlatedState = Math.max(
        -1,
        Math.min(1, correlation * this.correlatedState + Math.sqrt(1 - correlation * correlation) * innovation),
      );
    }
    return interpolateBounds(minimum, maximum, (this.correlatedState + 1) * 0.5);
  }
}

export class SeedTree {
  readonly version: PrngVersion;

  private readonly rootSeed: string;
  private readonly prefix: string;

  constructor(seed: Seed, options: SeedTreeOptions = {}, prefix = '') {
    const version = options.version ?? 'xorshift32-v1';
    if (!isVersion(version)) throw new RangeError(`unsupported PRNG version: ${String(version)}`);
    this.version = version;
    this.rootSeed = canonicalSeed(seed);
    this.prefix = prefix;
  }

  derive(name: string): number {
    assertName(name);
    const path = this.prefix ? `${this.prefix}/${name}` : name;
    return hashSeed(`${this.version}\u0000${this.rootSeed}\u0000${path}`);
  }

  stream(name: string): RandomStream {
    return new RandomStream(this.derive(name), this.version);
  }

  child(name: string): SeedTree {
    assertName(name);
    return new SeedTree(this.rootSeed, { version: this.version }, this.prefix ? `${this.prefix}/${name}` : name);
  }
}

class SeedTreeModuleInstance implements DspModuleInstance {
  private readonly maxBlockSize: number;
  private seed = 0;
  private stream = new SeedTree(0).stream('output');

  constructor(maxBlockSize: number) {
    this.maxBlockSize = maxBlockSize;
  }

  reset(): void { this.stream.reset(); }

  setParameter(id: string, value: number): void {
    if (id !== 'seed') throw new RangeError(`unknown parameter: ${id}`);
    if (!Number.isInteger(value) || value < 0 || value >= UINT32_RANGE) throw new RangeError('seed must be a uint32 integer');
    this.seed = value;
    this.stream = new SeedTree(this.seed).stream('output');
  }

  process(block: DspProcessBlock): void {
    if (!Number.isInteger(block.frames) || block.frames < 0 || block.frames > this.maxBlockSize) {
      throw new RangeError('frames exceeds the configured maximum block size');
    }
    const output = block.outputs.out;
    if (!output || output.length < block.frames) throw new RangeError('out buffer is missing or too short');
    for (let index = 0; index < block.frames; index += 1) output[index] = this.stream.next();
  }
}

/** Catalog/runtime adapter; all random behavior remains implemented by SeedTree. */
export const mod035 = defineDspModule({
  descriptor: {
    id: 'mir.mod-035',
    name: 'Deterministic seed tree / distributions',
    version: '0.1.0',
    inputs: [],
    outputs: [{ id: 'out', kind: 'control', channels: 1 }],
    parameters: [{ id: 'seed', defaultValue: 0, minValue: 0, maxValue: UINT32_RANGE - 1, unit: 'uint32' }],
  },
  createInstance(context) {
    if (!(context.sampleRate > 0) || !Number.isInteger(context.maxBlockSize) || context.maxBlockSize < 1) {
      throw new RangeError('invalid DSP instance context');
    }
    return new SeedTreeModuleInstance(context.maxBlockSize);
  },
});
